/**
 * "Which dataset?" — the first step of the two rail panels that act on one (client,
 * 2026-09-18).
 *
 * A searchable list of the manager's catalogue, `include_hidden` like the Datasets tab, with
 * the type glyph and the id beside every title: two datasets called "Soil pH" are told apart
 * by their id, not by their title, and the manager is the person who has to tell them apart.
 *
 * Two ways to keep a row out of reach. `include` drops it from the list altogether — the
 * Edit Data panel uses it for rasters (client, 2026-09-21: *"we will not typically edit
 * raster data"*), which are not a thing one edits row by row, so listing them greyed was
 * noise. `disabledReason` keeps a row **listed, greyed, with the reason** — for a vector or a
 * table the catalogue holds but whose rows it cannot serve, where dropping the row would
 * leave a manager hunting for a dataset that is right there in the catalogue.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { fetchDatasetsAll, queryKeys, type ManagedDatasetRow } from '../../lib/api';
import { Icon, type IconName } from '../../lib/icons';

const TYPE_ICON: Record<string, IconName> = {
  raster: 'raster',
  vector: 'vector',
  tabular: 'table',
};

export interface DatasetPickerProps {
  /** `null` while nothing is picked. */
  value: string | null;
  onPick: (row: ManagedDatasetRow) => void;
  /** Rows to list at all; the rest are left out, not greyed. Default: every row. */
  include?: (row: ManagedDatasetRow) => boolean;
  /** Why this row cannot be picked, or `null` when it can. */
  disabledReason?: (row: ManagedDatasetRow) => string | null;
  label: string;
  hint?: string;
  testId: string;
}

export function DatasetPicker({
  value,
  onPick,
  include,
  disabledReason,
  label,
  hint,
  testId,
}: DatasetPickerProps) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');

  const { data, isPending, isError } = useQuery({
    queryKey: queryKeys.datasetsAll(),
    queryFn: fetchDatasetsAll,
  });

  const rows = useMemo(
    () => (data?.results ?? []).filter((row) => include?.(row) ?? true),
    [data, include],
  );
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) =>
      `${row.title} ${row.id} ${row.source_org}`.toLowerCase().includes(needle),
    );
  }, [q, rows]);

  return (
    <div className="mgr-pick" data-testid={testId}>
      <label className="mgr-field" htmlFor={`${testId}-search`}>
        <span className="mgr-label">{label}</span>
        <input
          id={`${testId}-search`}
          data-testid={`${testId}-search`}
          className="mgr-input"
          type="search"
          value={q}
          placeholder={t('manager.ds.searchPlaceholder')}
          onChange={(e) => setQ(e.target.value)}
        />
        {hint ? <span className="mgr-hint">{hint}</span> : null}
      </label>

      {isPending ? <p className="rows-note">{t('manager.ds.loading')}</p> : null}
      {isError ? <p className="rows-note">{t('manager.ds.error')}</p> : null}
      {!isPending && !isError && shown.length === 0 ? (
        <p className="rows-note">{t('manager.ds.noMatch')}</p>
      ) : null}

      <ul className="mgr-pick-list">
        {shown.map((row) => {
          const why = disabledReason?.(row) ?? null;
          return (
            <li key={row.id}>
              <button
                type="button"
                className={`mgr-pick-row${value === row.id ? ' on' : ''}${why ? ' off' : ''}`}
                data-testid={`${testId}-${row.id}`}
                disabled={why !== null}
                aria-disabled={why !== null}
                onClick={() => onPick(row)}
              >
                {/* The glyph *and* its colour say which kind: a pixel grid, a polygon and a
                    headed table read alike at 16 px in one ink. */}
                <span className={`mgr-pick-ico ${row.data_type}`}>
                  <Icon name={TYPE_ICON[row.data_type] ?? 'db'} size={16} />
                </span>
                <span className="mgr-pick-text">
                  <strong>{row.title}</strong>
                  <em>{row.id}</em>
                </span>
                {why ? <span className="mgr-pick-why">{why}</span> : null}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default DatasetPicker;
